import {
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FindControlAdditionalSearchParametersDto,
  FindControlInitDto,
  FindControlRequiredSearchParametersDto,
  IColumnPk,
  IDeleted,
  ISelected,
} from 'webcommon/shared';
import {
  ApBaseComponent,
  ApUndoableOperationService,
} from 'webcommon/core-ui';
import { ApFindControlDeleteService } from '../delete/ap-findcontrol-delete.service';
import { FindControlDeleteAction } from '../delete/DeleteFindControlItemOperation';
import { FcItemModalMode, FcModifyModal, FcNewModal, FindControlMode } from '../../models/index';
import { FcItemModalService } from '../fc-item-modal.service';
import { Observable, Subject } from 'rxjs';
import { ApFindControlAdvancedUpdate, ApFindControlAdvancedUpdateAction } from '../ap-findcontrol-advanced/index';

@Component({
  selector: 'ap-findcontrol-generic',
  templateUrl: './ap-findcontrol-generic.component.html',
  styleUrls: ['./ap-findcontrol-generic.component.less'],
})
export class ApFindControlGenericComponent extends ApBaseComponent implements OnInit, OnDestroy {

  @Input() findControlId: string;
  @Input() baseId: string;
  @Input() requiredSearchParameters?: FindControlRequiredSearchParametersDto;
  @Input() additionalSearchParameters?: FindControlAdditionalSearchParametersDto;
  @Input() mode: FindControlMode;

  private findControlInitDto: FindControlInitDto;
  undoComponentId: number;
  private readonly updateObs$ = new Subject<ApFindControlAdvancedUpdate>();

  readonly updateObs: Observable<ApFindControlAdvancedUpdate> = this.updateObs$.asObservable();

  constructor(
    private apFindControlDeleteService: ApFindControlDeleteService,
    private apUndoableOperationService: ApUndoableOperationService,
    private fcItemModalService: FcItemModalService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.undoComponentId = this.apUndoableOperationService.registerComponent();
  }

  override ngOnDestroy(): void {
    this.apUndoableOperationService.unregisterComponent(this.undoComponentId);
  }

  onFindControlLoaded(findControlInitDto: FindControlInitDto) {
    this.findControlInitDto = findControlInitDto;
  }

  onNewClick() {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const context: FcNewModal = {
        mode: FcItemModalMode.New,
        findControlId: this.findControlInitDto.FindControlBehavior.Id,
        findControlName: this.findControlInitDto.FindControlBehavior.Name,
        findControlConfig: this.findControlInitDto,
      };
      const promise = this.fcItemModalService.openModal(context).then(result => {
        if (result.isSuccess) {
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.New,
            dataItem: result.resultValue,
          });
        }
      });
      return {
        completionPromise: promise,
      };
    });
  }

  onDelete(dataItem: IColumnPk & IDeleted) {
    this.apFindControlDeleteService.deleteItem(
      this.undoComponentId,
      this.findControlInitDto.FindControlBehavior.Id,
      this.findControlInitDto.FindControlBehavior.Name,
      this.findControlInitDto.FindControlBehavior.ColumnsConfig.PrimaryKey,
      dataItem
    ).pipe(
      this.takeUntilDestroy(),
    ).subscribe((checkpoint) => {
      switch (checkpoint.action) {
        case FindControlDeleteAction.Predelete: {
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.Hide,
            dataItem,
          });
          break;
        }
        case FindControlDeleteAction.DeleteFailure:
        case FindControlDeleteAction.Undo: {
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.Show,
            dataItem,
          });
          break;
        }
      }
    });
  }

  onModify(dataItem: IColumnPk & ISelected) {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const context: FcModifyModal = {
        mode: FcItemModalMode.Modify,
        findControlId: this.findControlInitDto.FindControlBehavior.Id,
        findControlName: this.findControlInitDto.FindControlBehavior.Name,
        selectedFindItemPk: dataItem.Pk,
        selectedFindItemName: dataItem[this.findControlInitDto.FindControlBehavior.ColumnsConfig.PrimaryDisplayName],
        findControlConfig: this.findControlInitDto,
      };
      const promise = this.fcItemModalService.openModal(context).then(result => {
        if (result.isSuccess) {
          this.updateObs$.next({
            action: ApFindControlAdvancedUpdateAction.Modify,
            dataItem: result.resultValue,
          });
        }
      });
      return {
        completionPromise: promise,
      };
    });
  }
}
